<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useConfig } from 'dashboard/composables/useConfig';
import Modal from '../../../../components/Modal.vue';
import PipelineCardForm from './PipelineCardForm.vue';
import PipelineSelector from './PipelineSelector.vue';
import PipelineSettings from './PipelineSettings.vue';
import PipelineFollowUpRules from './PipelineFollowUpRules.vue';
import PipelineWebhooks from './PipelineWebhooks.vue';
import BulkDeleteModal from './BulkDeleteModal.vue';
import BulkMoveModal from './BulkMoveModal.vue';
import BulkAddModal from './BulkAddModal.vue';
import BulkPriorityModal from './BulkPriorityModal.vue';
import PipelineCardsAPI from 'dashboard/api/pipelineCards';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import PipelineForm from './PipelineForm.vue';
import BulkSendMessageModal from './BulkSendMessageModal.vue';
import PipelineCardImportModal from './PipelineCardImportModal.vue';
import PipelineTrashModal from './PipelineTrashModal.vue';
import AIConfigModal from './AIConfigModal.vue';
import KeyboardShortcutsModal from './KeyboardShortcutsModal.vue';
import { downloadBlob } from 'dashboard/helper/downloadHelper';
import { isSet, parseDateOnlyLocal } from 'dashboard/helper/filterHelper';
import { emitter } from 'shared/helpers/mitt';
import { usePipelineKeyboard } from 'dashboard/composables/usePipelineKeyboard';
import { getCardCurrency, getCardValue } from '../helpers/columnFilterMatchers';
import { dismissMenuOnFocusOut } from '../helpers/menuDismissal';
import { formatLocalizedCurrency } from '../utils/localeFormatting';
import { getStageColorClasses } from './pipelineTemplates';

const props = defineProps({
  currentStage: {
    type: String,
    default: '',
  },
  searchResults: {
    type: Object,
    default: () => ({ total: 0, stages: {} }),
  },
  columns: {
    type: Array,
    default: () => [],
  },
  activeFilters: {
    type: Object,
    default: null,
  },
  searchValue: {
    type: String,
    default: '',
  },
  currentView: {
    type: String,
    default: 'pipeline',
  },
  pipelineCards: {
    type: Array,
    required: true,
  },
  hasPartialDataset: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'filter',
  'itemCreated',
  'search',
  'itemsUpdated',
  'switchView',
  'newToastMessage',
  'startTour',
]);

const notify = payload => {
  emitter.emit('newToastMessage', payload);
  emit('newToastMessage', payload);
};

// Função utilitária de debounce
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

const { t, locale } = useI18n();
const store = useStore();
const { isStacklab } = useConfig();

// User-scoped localStorage key prefix to isolate data between users
const currentUserId = computed(() => store.getters.getCurrentUserID || 0);
const currentAccountId = computed(() => store.getters.getCurrentAccountId || 0);
const currentAccount = computed(
  () =>
    store.getters['accounts/getAccount']?.(currentAccountId.value) ||
    store.getters.getCurrentAccount ||
    {}
);
const accountCurrency = computed(() =>
  getCardCurrency(
    {
      currency: currentAccount.value.currency,
      item_details: { currency: currentAccount.value.settings?.currency },
    },
    'BRL'
  )
);
const getUserScopedKey = columnId =>
  `u${currentUserId.value}_a${currentAccountId.value}_pipeline_column_${columnId}_settings`;

const showAddModal = ref(false);
const addCardFormRef = ref(null);
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);

const showSettingsModal = ref(false);
const showFollowUpRulesModal = ref(false);
const showWebhooksModal = ref(false);
const showImportModal = ref(false); // NOOVI: import CSV de cards (Gap C — fase-10)
const showTrashModal = ref(false); // NOOVI: lixeira por pipeline (admin-only)
const showAIConfigModal = ref(false);
const showKeyboardShortcutsModal = ref(false);

const searchQuery = ref('');

// O board mantém a busca junto dos filtros server-side. Sincronizar o valor
// permite remount/troca de view sem exibir um campo vazio sobre dados filtrados.
watch(
  () => props.searchValue,
  value => {
    searchQuery.value = value || '';
  },
  { immediate: true }
);

const showBulkActions = ref(false);
const bulkActionsPopoverRef = ref(null);
const bulkActionsButtonRef = ref(null);
const bulkActionsDropdownRef = ref(null);
const isBulkSubmitting = ref(false);
watch(showBulkActions, isOpen => {
  if (!isOpen) bulkActionsPopoverRef.value?.hide();
});

const showHiddenStages = ref(false);

const bulkActions = computed(() => [
  {
    id: 'add',
    label: t('PIPELINE.BULK_ACTIONS.ADD.TITLE'),
    icon: 'add',
  },
  {
    id: 'move',
    label: t('PIPELINE.BULK_ACTIONS.MOVE.TITLE'),
    icon: 'arrow-right',
  },
  {
    id: 'set_priority',
    label: t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.TITLE'),
    icon: 'flag',
  },
  {
    id: 'delete',
    label: t('PIPELINE.BULK_ACTIONS.DELETE.TITLE'),
    icon: 'delete',
  },
  {
    id: 'show_hidden',
    label: t('PIPELINE.BULK_ACTIONS.SHOW_HIDDEN'),
    icon: 'eye-show',
  },
  {
    id: 'send_message',
    label: t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.TITLE'),
    icon: 'chat',
  },
]);

const showBulkDeleteModal = ref(false);
const showBulkMoveModal = ref(false);
const showBulkAddModal = ref(false);
const showBulkPriorityModal = ref(false);

const updateBulkDeleteModal = show => {
  if (!isBulkSubmitting.value || show) showBulkDeleteModal.value = show;
};
const updateBulkMoveModal = show => {
  if (!isBulkSubmitting.value || show) showBulkMoveModal.value = show;
};
const updateBulkPriorityModal = show => {
  if (!isBulkSubmitting.value || show) showBulkPriorityModal.value = show;
};

const showSettingsDropdown = ref(false);
const showAddDropdown = ref(false);
const addButtonRef = ref(null);
const settingsButtonRef = ref(null);

const focusElement = targetRef => {
  const target = targetRef?.value?.$el || targetRef?.value;
  if (target instanceof HTMLElement) {
    const focusTarget = target.matches('button')
      ? target
      : target.querySelector('button');
    focusTarget?.focus();
  }
};

const focusFirstMenuItem = id => {
  document.getElementById(id)?.querySelector('button:not([disabled])')?.focus();
};

// Account-aware (achado Codex): papel BASE 'administrator' + custom_role_id que
// não concede admin é uma divergência que já vazou gates admin-only em prod.
// Admin de verdade = papel administrator E sem custom role (o backend/Pundit é
// o guarda real; isto só evita exibir ações admin-only — import/export/webhooks/
// lixeira — a quem não é admin).
const userIsAdmin = computed(
  () =>
    store.getters.getCurrentRole === 'administrator' &&
    !store.getters.getCurrentCustomRoleId
);

const currentAccountPermissions = computed(() => {
  return Array.isArray(currentAccount.value?.permissions)
    ? currentAccount.value.permissions
    : [];
});

const canManageFollowUpRules = computed(
  () =>
    userIsAdmin.value ||
    currentAccountPermissions.value.includes('follow_up_manage')
);

const filteredBulkActions = computed(() =>
  bulkActions.value.filter(action => {
    if (action.id === 'send_message') return isStacklab === true;
    if (action.id === 'delete') return userIsAdmin.value;
    return true;
  })
);

const settingsOptions = computed(() => {
  const options = [];

  if (userIsAdmin.value) {
    options.push({
      id: 'pipeline_settings',
      label: t('PIPELINE.MORE_OPTIONS.PIPELINE_SETTINGS'),
      icon: 'settings',
    });
    options.push({
      id: 'ai_config',
      label: t('PIPELINE.AI_CONFIG.TITLE'),
      icon: 'sparkles',
    });
  }

  if (isStacklab === true) {
    options.push({
      id: 'message_templates',
      label: t('PIPELINE.MORE_OPTIONS.MESSAGE_TEMPLATES'),
      icon: 'document',
    });
  }

  if (canManageFollowUpRules.value) {
    options.push({
      id: 'follow_up_rules',
      label: t('PIPELINE.MORE_OPTIONS.FOLLOW_UP_RULES'),
      icon: 'calendar-clock',
    });
  }

  // Add Webhooks option (admin only)
  if (userIsAdmin.value) {
    options.push({
      id: 'webhooks',
      label: t('PIPELINE_WEBHOOK.TITLE'),
      icon: 'webhook',
    });
  }

  // NOOVI: Import de cards via CSV (admin only) — Gap C — fase-10.
  if (userIsAdmin.value) {
    options.push({
      id: 'import_cards',
      label: t('PIPELINE.IMPORT.MENU_LABEL'),
      icon: 'arrow-upload',
    });
  }

  // NOOVI: Lixeira por pipeline (admin only) — cards descartados, restauração e
  // exclusão definitiva. Retenção de 30 dias.
  if (userIsAdmin.value) {
    options.push({
      id: 'trash',
      label: t('PIPELINE.TRASH.MENU_LABEL'),
      icon: 'trash',
    });
  }

  // NOOVI: Export de cards para CSV (Gap C — export). Disponível a todos —
  // o backend respeita policy_scope (cada um exporta só o que enxerga).
  options.push({
    id: 'export_cards',
    label: t('PIPELINE.EXPORT.MENU_LABEL'),
    icon: 'arrow-download',
  });

  if (userIsAdmin.value) {
    options.unshift({
      id: 'manage_pipelines',
      label: t('PIPELINE.MORE_OPTIONS.MANAGE_PIPELINES'),
      icon: 'folder',
    });
  }

  // Add Start Tour option at the end
  options.push({
    id: 'start_tour',
    label: t('PIPELINE.TOUR.START'),
    icon: 'info',
  });

  return options;
});

const closeDropdowns = () => {
  showSettingsDropdown.value = false;
  showAddDropdown.value = false;
  showBulkActions.value = false;
  bulkActionsPopoverRef.value?.hide();
};

const closeMenuAfterFocusLeaves = event => {
  dismissMenuOnFocusOut(event, closeDropdowns);
};

const handleMenuKeydown = (event, triggerRef) => {
  const items = [
    ...event.currentTarget.querySelectorAll('button:not([disabled])'),
  ];
  const currentIndex = items.indexOf(document.activeElement);
  let targetIndex = null;

  if (event.key === 'ArrowDown') targetIndex = currentIndex + 1;
  if (event.key === 'ArrowUp') targetIndex = currentIndex - 1;
  if (event.key === 'Home') targetIndex = 0;
  if (event.key === 'End') targetIndex = items.length - 1;

  if (targetIndex !== null && items.length) {
    event.preventDefault();
    items[(targetIndex + items.length) % items.length].focus();
  } else if (event.key === 'Escape') {
    event.preventDefault();
    closeDropdowns();
    nextTick(() => focusElement(triggerRef));
  }
};

const handleMessageTemplates = () => {
  emit('switchView', 'templates');
};

// NOOVI: exporta os cards (CSV) respeitando os filtros ativos do board.
const isExporting = ref(false);
const filenameFromDisposition = disposition => {
  const match = /filename="?([^"]+)"?/.exec(disposition || '');
  return match?.[1];
};
const exportCards = async () => {
  if (isExporting.value) return;
  isExporting.value = true;
  try {
    const pipelineId = selectedPipeline.value?.id;
    const response = await PipelineCardsAPI.exportCards(
      pipelineId,
      props.activeFilters
    );
    // Preserva o nome com timestamp que o backend define no Content-Disposition.
    const fileName =
      filenameFromDisposition(response.headers?.['content-disposition']) ||
      'pipeline_cards_export.csv';
    downloadBlob(fileName, response.data);
  } catch (error) {
    notify({
      message: t('PIPELINE.EXPORT.ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isExporting.value = false;
  }
};

const handleSettingsOption = option => {
  if (option.id === 'pipeline_settings') {
    showSettingsModal.value = true;
  } else if (option.id === 'ai_config') {
    showAIConfigModal.value = true;
  } else if (option.id === 'manage_pipelines') {
    emit('switchView', 'pipelines');
  } else if (option.id === 'message_templates') {
    handleMessageTemplates();
  } else if (option.id === 'follow_up_rules') {
    showFollowUpRulesModal.value = true;
  } else if (option.id === 'webhooks') {
    showWebhooksModal.value = true;
  } else if (option.id === 'import_cards') {
    showImportModal.value = true;
  } else if (option.id === 'trash') {
    showTrashModal.value = true;
  } else if (option.id === 'export_cards') {
    exportCards();
  } else if (option.id === 'start_tour') {
    emit('startTour');
  }
  showSettingsDropdown.value = false;
};

const handleSettingsClick = () => {
  const wasOpen = showSettingsDropdown.value;
  closeDropdowns();
  if (!wasOpen) {
    emitter.emit('closeAllPipelineDropdowns');
  }
  showSettingsDropdown.value = !wasOpen;
  if (showSettingsDropdown.value) {
    nextTick(() => focusFirstMenuItem('pipeline-settings-menu'));
  }
};

const handleBulkActions = () => {
  const wasOpen = showBulkActions.value;
  closeDropdowns();
  if (!wasOpen) {
    emitter.emit('closeAllPipelineDropdowns');
  }
  showBulkActions.value = !wasOpen;
  if (showBulkActions.value) {
    bulkActionsPopoverRef.value?.show();
    nextTick(() => {
      focusFirstMenuItem('pipeline-bulk-actions-menu');
    });
  } else {
    bulkActionsPopoverRef.value?.hide();
  }
};

const showSendMessageModal = ref(false);

// Função para filtrar itens com conversa
const itemsWithConversation = computed(() => {
  return props.pipelineCards.filter(item => item.item_details?.conversation_id);
});

const selectBulkAction = action => {
  if (action.id === 'show_hidden') {
    showHiddenStages.value = !showHiddenStages.value;

    // Verifica se existem colunas ocultas antes de alterar (user-scoped)
    const hasHiddenColumns = props.columns.some(column => {
      const settings = JSON.parse(
        localStorage.getItem(getUserScopedKey(column.id)) || '{}'
      );
      return settings.hideColumn === true;
    });

    // Só altera se existirem colunas ocultas ou se estiver desativando
    if (hasHiddenColumns || !showHiddenStages.value) {
      props.columns.forEach(column => {
        const settings = JSON.parse(
          localStorage.getItem(getUserScopedKey(column.id)) || '{}'
        );

        // Só altera se a coluna estiver oculta
        if (settings.hideColumn === true) {
          settings.hideColumn = !showHiddenStages.value;
          localStorage.setItem(
            getUserScopedKey(column.id),
            JSON.stringify(settings)
          );
        }
      });

      emit('itemsUpdated');
    }
  } else if (action.id === 'delete') {
    showBulkDeleteModal.value = true;
  } else if (action.id === 'move') {
    showBulkMoveModal.value = true;
  } else if (action.id === 'add') {
    showBulkAddModal.value = true;
  } else if (action.id === 'set_priority') {
    showBulkPriorityModal.value = true;
  } else if (action.id === 'send_message') {
    if (itemsWithConversation.value.length === 0) {
      notify({
        message: t('PIPELINE.NO_ITEMS_WITH_CONVERSATION'),
        action: { type: 'warning' },
      });
      return;
    }
    showSendMessageModal.value = true;
  }
  showBulkActions.value = false;
};

const handleCloseSettings = () => {
  showSettingsModal.value = false;
};

const showNewPipelineModal = ref(false);
const newPipelineFormRef = ref(null);

const handleAdd = () => {
  const wasOpen = showAddDropdown.value;
  closeDropdowns();
  if (!wasOpen) {
    emitter.emit('closeAllPipelineDropdowns');
  }
  showAddDropdown.value = !wasOpen;
  if (showAddDropdown.value) {
    nextTick(() => focusFirstMenuItem('pipeline-add-menu'));
  }
};

const handleNewItem = () => {
  if (!selectedPipeline.value?.id) {
    notify({
      message: t('PIPELINE.ERRORS.NO_PIPELINE_SELECTED'),
      action: { type: 'error' },
    });
    return;
  }
  showAddModal.value = true;
  showAddDropdown.value = false;
};

const handleNewPipeline = () => {
  if (!userIsAdmin.value) return;
  showNewPipelineModal.value = true;
  showAddDropdown.value = false;
};

const handlePipelineSaved = () => {
  showNewPipelineModal.value = false;
  emit('itemsUpdated');
};

const handleItemCreated = async item => {
  emit('itemCreated', item);
  showAddModal.value = false;
};

const requestAddCardModalClose = () =>
  addCardFormRef.value?.requestClose?.() ?? true;

const requestNewPipelineModalClose = () =>
  newPipelineFormRef.value?.requestClose?.() ?? true;

const focusPipelineSearch = () => {
  document.getElementById('pipeline-search')?.focus();
};

usePipelineKeyboard({
  onNewCard: handleNewItem,
  onSearch: focusPipelineSearch,
  onShowShortcuts: () => {
    showKeyboardShortcutsModal.value = true;
  },
  onEscape: closeDropdowns,
});

const onSearch = debounce(e => {
  const query = e.target.value;
  searchQuery.value = query;
  emit('search', query);
}, 300);

const handleBulkDelete = async payload => {
  // Fase 4.1 — payload now is `{ cardIds, pipelineStage }` (object).
  // Backward-compat: if an old caller still sends just an array, normalize.
  const { cardIds, pipelineStage = null } = Array.isArray(payload)
    ? { cardIds: payload }
    : payload || {};
  if (isBulkSubmitting.value || !cardIds?.length) return;
  isBulkSubmitting.value = true;
  try {
    // Single bulk endpoint replaces N parallel deleteItem requests
    await PipelineCardsAPI.bulkDelete({ cardIds, pipelineStage });
    showBulkDeleteModal.value = false;
    emit('itemsUpdated');
  } catch {
    notify({
      message: t('PIPELINE.BULK_ACTIONS.ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isBulkSubmitting.value = false;
  }
};

const handleBulkSetPriority = async payload => {
  // Fase 4.2 — payload is { cardIds, pipelineStage, priority }
  const { cardIds, pipelineStage = null, priority } = payload || {};
  if (isBulkSubmitting.value || !cardIds?.length || !priority) return;
  isBulkSubmitting.value = true;
  try {
    const { data } = await PipelineCardsAPI.bulkSetPriority({
      cardIds,
      pipelineStage,
      priority,
    });
    // Atualize localmente somente os IDs confirmados pelo backend. O escopo
    // por etapa, visibilidade e validações pode reduzir o conjunto solicitado.
    const hasExactUpdatedIds = Array.isArray(data?.updated_ids);
    const updatedIds = hasExactUpdatedIds ? data.updated_ids : [];
    updatedIds.forEach(id => {
      store.commit('pipelineCards/UPDATE_PIPELINE_CARD_ITEM', {
        id,
        priority,
        item_details: {
          ...(store.getters['pipelineCards/getPipelineCardItems'].find(
            c => c.id === id
          )?.item_details || {}),
          priority,
        },
      });
    });
    showBulkPriorityModal.value = false;
    emit('itemsUpdated');

    if (
      hasExactUpdatedIds &&
      (updatedIds.length < cardIds.length || Number(data?.failed) > 0)
    ) {
      notify({
        message: t('PIPELINE.BULK_ACTIONS.SET_PRIORITY.PARTIAL', {
          updated: updatedIds.length,
          requested: cardIds.length,
        }),
        action: { type: 'warning' },
      });
    }
  } catch {
    notify({
      message: t('PIPELINE.BULK_ACTIONS.ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isBulkSubmitting.value = false;
  }
};

const handleBulkMove = async ({ itemIds, stageId }) => {
  if (isBulkSubmitting.value || !itemIds?.length || !stageId) return;
  isBulkSubmitting.value = true;
  try {
    await Promise.all(
      itemIds.map(id => PipelineCardsAPI.moveToStage(id, stageId))
    );
    showBulkMoveModal.value = false;
    emit('itemsUpdated');
  } catch {
    notify({
      message: t('PIPELINE.BULK_ACTIONS.ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isBulkSubmitting.value = false;
  }
};

const handleBulkItemsCreated = async () => {
  showBulkAddModal.value = false;
  emit('itemsUpdated');
};

const getStageColorClassesByName = stageName => {
  const column = props.columns.find(col => col.title === stageName);
  return getStageColorClasses(column?.color);
};

// Computed para contar filtros ativos
const activeFiltersCount = computed(() => {
  if (!props.activeFilters) return 0;

  let count = 0;
  if (props.activeFilters.labels?.length) count += 1;
  if (props.activeFilters.priority?.length) count += 1;
  if (props.activeFilters.stages?.length) count += 1;
  if (
    isSet(props.activeFilters.value?.min) ||
    isSet(props.activeFilters.value?.max)
  )
    count += 1;
  if (props.activeFilters.agent_id) count += 1;
  if (props.activeFilters.date?.start || props.activeFilters.date?.end)
    count += 1;
  if (props.activeFilters.status) count += 1;
  if (props.activeFilters.sla_exceeded) count += 1;

  return count;
});

// Computed para gerar descrição dos filtros ativos
const activeFiltersTooltip = computed(() => {
  if (!props.activeFilters || activeFiltersCount.value === 0) {
    return t('PIPELINE.FILTERS.NO_FILTERS');
  }

  const descriptions = [];

  if (props.activeFilters.labels?.length) {
    descriptions.push(
      `${t('PIPELINE.FILTER.LABELS.LABEL')}: ${props.activeFilters.labels.join(', ')}`
    );
  }

  if (props.activeFilters.priority?.length) {
    const priorities = props.activeFilters.priority
      .map(p => t(`PIPELINE.PRIORITY_LABELS.${p.toUpperCase()}`))
      .join(', ');
    descriptions.push(`${t('PIPELINE.FORM.PRIORITY.LABEL')}: ${priorities}`);
  }

  if (
    isSet(props.activeFilters.value?.min) ||
    isSet(props.activeFilters.value?.max)
  ) {
    const min = isSet(props.activeFilters.value.min)
      ? props.activeFilters.value.min
      : '0';
    const max = isSet(props.activeFilters.value.max)
      ? props.activeFilters.value.max
      : '∞';
    descriptions.push(`${t('PIPELINE.FORM.VALUE.LABEL')}: ${min} - ${max}`);
  }

  if (props.activeFilters.agent_id) {
    const agent = selectedPipeline.value?.settings?.agents?.find(
      a => a.id === props.activeFilters.agent_id
    );
    const agentName = agent?.name || t('PIPELINE.FORM.AGENT.LABEL');
    descriptions.push(`${t('PIPELINE.FORM.AGENT.LABEL')}: ${agentName}`);
  }

  if (props.activeFilters.date?.start || props.activeFilters.date?.end) {
    // parse local + formato fixo dd/MM/yyyy (toLocaleDateString seguia o
    // locale do browser e o parse UTC recuava 1 dia — QA ciclo 9).
    const formatChipDate = value => {
      const parsed = parseDateOnlyLocal(value);
      if (!parsed) return '...';
      const dd = String(parsed.getDate()).padStart(2, '0');
      const mm = String(parsed.getMonth() + 1).padStart(2, '0');
      return `${dd}/${mm}/${parsed.getFullYear()}`;
    };
    const start = props.activeFilters.date.start
      ? formatChipDate(props.activeFilters.date.start)
      : '...';
    const end = props.activeFilters.date.end
      ? formatChipDate(props.activeFilters.date.end)
      : '...';
    descriptions.push(`${t('PIPELINE.DEADLINE')}: ${start} - ${end}`);
  }

  if (props.activeFilters.stages?.length) {
    descriptions.push(
      `${t('PIPELINE.STAGE.LABEL')}: ${props.activeFilters.stages.join(', ')}`
    );
  }

  if (props.activeFilters.status) {
    descriptions.push(
      `${t('PIPELINE.FILTERS.STATUS.LABEL')}: ${t(`PIPELINE.FILTERS.STATUS.${props.activeFilters.status.toUpperCase()}`)}`
    );
  }

  if (props.activeFilters.sla_exceeded) {
    descriptions.push(t('PIPELINE.SLA.FILTER_LABEL'));
  }

  return descriptions.join('\n');
});

const handleViewChange = view => {
  emit('switchView', view);
};

// Map bulk action icon names to Lucide icons
const getBulkActionIcon = iconName => {
  const iconMap = {
    add: 'i-lucide-plus',
    'arrow-right': 'i-lucide-arrow-right',
    flag: 'i-lucide-flag',
    delete: 'i-lucide-trash-2',
    'eye-show': 'i-lucide-eye',
    chat: 'i-lucide-message-circle',
  };
  return iconMap[iconName] || 'i-lucide-circle';
};

// Map settings option icon names to Lucide icons
const getSettingsIcon = iconName => {
  const iconMap = {
    settings: 'i-lucide-settings',
    document: 'i-lucide-file-text',
    folder: 'i-lucide-folder',
    clock: 'i-lucide-clock',
    'calendar-clock': 'i-lucide-calendar-clock',
    'arrow-upload': 'i-lucide-upload',
    'arrow-download': 'i-lucide-download',
    info: 'i-lucide-info',
    sparkles: 'i-lucide-sparkles',
    webhook: 'i-lucide-webhook',
    trash: 'i-lucide-trash-2',
  };
  return iconMap[iconName] || 'i-lucide-circle';
};

const handleDocumentClick = event => {
  const target = event.target;
  const isDropdownClick =
    target instanceof Element &&
    target.closest(
      '.settings-selector, .bulk-actions-selector, .pipeline-add-item-trigger'
    );

  if (!isDropdownClick) closeDropdowns();
};

const handleDocumentKeydown = event => {
  if (event.key === 'Escape') closeDropdowns();
};

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  document.addEventListener('keydown', handleDocumentKeydown);

  // Listen for global dropdown close event
  emitter.on('closeAllPipelineDropdowns', closeDropdowns);
});

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick);
  document.removeEventListener('keydown', handleDocumentKeydown);
  emitter.off('closeAllPipelineDropdowns', closeDropdowns);
});

// Values in different currencies are intentionally kept separate. Adding them
// together would present a financially incorrect total.
const openCards = computed(() =>
  props.pipelineCards.filter(card => (card.status || 'open') === 'open')
);

const totalPipelineValues = computed(() => {
  return openCards.value.reduce((totals, card) => {
    const value = getCardValue(card);
    if (value === null) return totals;

    const currency = getCardCurrency(card, accountCurrency.value);
    totals[currency] = (totals[currency] || 0) + value;
    return totals;
  }, {});
});

const formattedPipelineTotal = computed(() => {
  return Object.entries(totalPipelineValues.value)
    .map(([currency, total]) => {
      try {
        return formatLocalizedCurrency(total, {
          currency,
          locale: locale.value,
        });
      } catch {
        return `${currency} ${total}`;
      }
    })
    .join(' · ');
});

// ── Métricas da barra ──────────────────────────────────────────────────────
// Calculadas sobre o dataset já carregado e exibidas sob a MESMA condição do
// total (`!hasPartialDataset`): com o board paginado, qualquer agregado seria
// uma fração apresentada como se fosse o todo.
// Um card é "parado" quando está aberto e entrou na etapa atual há mais de
// STALLED_DAYS. `stage_entered_at` já vem no payload do board — é tempo NA
// ETAPA, não idade do card, que é o que interessa para diagnosticar funil.
const STALLED_DAYS = 14;

// `Date.now()` não é reativo — sem isto, o card que cruza o limiar com o board
// aberto só passaria a contar quando algum outro dado mudasse.
const stalledClock = ref(Date.now());
let stalledTimer = null;
onMounted(() => {
  stalledTimer = setInterval(
    () => {
      stalledClock.value = Date.now();
    },
    30 * 60 * 1000
  );
});
onUnmounted(() => {
  if (stalledTimer) clearInterval(stalledTimer);
});

const openCardsCount = computed(
  () =>
    props.pipelineCards.filter(card => (card.status || 'open') === 'open')
      .length
);

// Taxa de ganho clássica: ganhos ÷ (ganhos + perdidos). Fica nula enquanto
// nenhum card foi fechado — 0% ali significaria "perdemos tudo", que é falso.
const winRate = computed(() => {
  const won = props.pipelineCards.filter(card => card.status === 'won').length;
  const lost = props.pipelineCards.filter(
    card => card.status === 'lost'
  ).length;
  if (won + lost === 0) return null;
  return Math.round((won / (won + lost)) * 100);
});

const stalledCount = computed(() => {
  const limit = stalledClock.value - STALLED_DAYS * 24 * 60 * 60 * 1000;
  return openCards.value.filter(card => {
    // Sem `stage_entered_at` não há evidência de tempo NA ETAPA: o card fica
    // fora da conta em vez de ser presumido parado. `created_at` não serve de
    // substituto — marcaria como parado o card antigo recém-movido, e num
    // deploy rolling (serializer antigo, sem o campo) marcaria quase todos.
    if (!card.stage_entered_at) return false;
    const timestamp = new Date(card.stage_entered_at).getTime();
    return Number.isFinite(timestamp) && timestamp < limit;
  }).length;
});

const winRateLabel = computed(() =>
  winRate.value === null ? '' : `${winRate.value}%`
);

const showBarMetrics = computed(
  () => !props.hasPartialDataset && props.pipelineCards.length > 0
);

// Função para enviar mensagem em massa
const handleMessageSent = async () => {
  showSendMessageModal.value = false;
  notify({
    message: t('PIPELINE.MESSAGES.MESSAGES_SENT_SUCCESS'),
    action: { type: 'success' },
  });
};

// Adicionar ref para controlar modal de visualização
const showViewModal = ref(false);
</script>

<template>
  <header
    class="relative z-50 flex min-w-0 flex-wrap items-center gap-2 border-b border-n-weak bg-n-background px-2 py-2 md:min-h-11 md:flex-nowrap md:px-3"
  >
    <!-- LEFT ZONE: View switcher + Pipeline context -->
    <div
      class="order-1 flex w-full min-w-0 flex-1 items-center gap-2 md:w-auto"
    >
      <!-- Mobile: View selector button -->
      <button
        type="button"
        class="flex min-h-11 min-w-11 items-center justify-center gap-1 rounded-lg border border-n-weak bg-n-alpha-2 px-2 text-n-slate-12 hover:bg-n-alpha-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9 active:bg-n-alpha-4 md:hidden"
        :aria-label="t('PIPELINE.VIEW_MODES.SELECT_VIEW')"
        aria-haspopup="dialog"
        @click="showViewModal = true"
      >
        <Icon
          :icon="
            currentView === 'pipeline'
              ? 'i-lucide-kanban'
              : currentView === 'list'
                ? 'i-lucide-list'
                : currentView === 'agenda'
                  ? 'i-lucide-calendar'
                  : 'i-lucide-layout-dashboard'
          "
          class="size-4"
        />
        <Icon icon="i-lucide-chevron-down" class="size-3" />
      </button>

      <!-- Desktop: View mode pill tabs -->
      <nav
        class="hidden items-center rounded-lg bg-n-alpha-1 p-0.5 md:flex"
        :aria-label="t('PIPELINE.VIEW_MODES.SELECT_VIEW')"
      >
        <button
          v-for="view in [
            {
              id: 'pipeline',
              icon: 'i-lucide-kanban',
              label: t('PIPELINE.VIEW_MODES.PIPELINE'),
              desc: t('PIPELINE.VIEW_MODES.PIPELINE_DESC'),
            },
            {
              id: 'list',
              icon: 'i-lucide-list',
              label: t('PIPELINE.VIEW_MODES.LIST'),
              desc: t('PIPELINE.VIEW_MODES.LIST_DESC'),
            },
            {
              id: 'agenda',
              icon: 'i-lucide-calendar',
              label: t('PIPELINE.VIEW_MODES.AGENDA'),
              desc: t('PIPELINE.VIEW_MODES.AGENDA_DESC'),
            },
            {
              id: 'dashboard',
              icon: 'i-lucide-layout-dashboard',
              label: t('PIPELINE.VIEW_MODES.DASHBOARD'),
              desc: t('PIPELINE.VIEW_MODES.DASHBOARD_DESC'),
            },
          ]"
          :key="view.id"
          v-tooltip="{
            content: view.desc,
            delay: { show: 400, hide: 0 },
            placement: 'bottom',
          }"
          type="button"
          class="flex h-11 min-w-11 fine-pointer:h-8 fine-pointer:min-w-8 items-center justify-center gap-1.5 rounded-md px-1.5 xl:px-2.5 transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
          :class="[
            currentView === view.id
              ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2',
          ]"
          :aria-label="view.label"
          :aria-pressed="currentView === view.id"
          @click="handleViewChange(view.id)"
        >
          <Icon :icon="view.icon" class="size-3.5 shrink-0" />
          <span class="hidden xl:inline text-xs font-medium">{{
            view.label
          }}</span>
        </button>
      </nav>

      <!-- Separator -->
      <div class="hidden md:block w-px h-4 bg-n-weak" />

      <!-- Pipeline selector -->
      <PipelineSelector class="min-w-0 flex-1 md:flex-none" />

      <!-- Métricas da barra. O total ganhou rótulo: antes era um "R$ 5.500,00"
           solto, sem dizer que se tratava do valor EM ABERTO. Conversão e
           parados entram ao lado, e todos somem juntos quando o dataset está
           paginado (`hasPartialDataset`). -->
      <div v-if="showBarMetrics" class="hidden lg:flex items-center gap-3">
        <span
          v-if="formattedPipelineTotal"
          v-tooltip="{
            content: t('PIPELINE.COLUMN_OPTIONS.TOTAL_VALUE_TOOLTIP', {
              count: openCardsCount,
              total: formattedPipelineTotal,
            }),
            delay: { show: 300, hide: 0 },
            placement: 'bottom',
          }"
          class="flex items-baseline gap-1 cursor-help whitespace-nowrap"
        >
          <span class="text-xs font-semibold text-n-slate-12 tabular-nums">
            {{ formattedPipelineTotal }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('PIPELINE.HEADER_METRICS.OPEN') }}
          </span>
          <span class="sr-only">
            {{
              t('PIPELINE.COLUMN_OPTIONS.TOTAL_VALUE_TOOLTIP', {
                count: openCardsCount,
                total: formattedPipelineTotal,
              })
            }}
          </span>
        </span>

        <span
          v-if="winRate !== null"
          v-tooltip="{
            content: t('PIPELINE.HEADER_METRICS.WIN_RATE_TOOLTIP'),
            delay: { show: 300, hide: 0 },
            placement: 'bottom',
          }"
          class="hidden xl:flex items-baseline gap-1 cursor-help whitespace-nowrap"
        >
          <span class="text-xs font-semibold text-n-slate-12 tabular-nums">
            {{ winRateLabel }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('PIPELINE.HEADER_METRICS.WIN_RATE') }}
          </span>
          <span class="sr-only">
            {{ t('PIPELINE.HEADER_METRICS.WIN_RATE_TOOLTIP') }}
          </span>
        </span>

        <span
          v-if="stalledCount > 0"
          v-tooltip="{
            content: t('PIPELINE.HEADER_METRICS.STALLED_TOOLTIP', {
              days: STALLED_DAYS,
              open: openCardsCount,
            }),
            delay: { show: 300, hide: 0 },
            placement: 'bottom',
          }"
          class="flex items-baseline gap-1 cursor-help whitespace-nowrap"
        >
          <span class="text-xs font-semibold text-n-amber-11 tabular-nums">
            {{ stalledCount }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('PIPELINE.HEADER_METRICS.STALLED') }}
          </span>
          <span class="sr-only">
            {{
              t('PIPELINE.HEADER_METRICS.STALLED_TOOLTIP', {
                days: STALLED_DAYS,
                open: openCardsCount,
              })
            }}
          </span>
        </span>
      </div>
    </div>

    <!-- RIGHT ZONE: Search + Actions -->
    <div
      class="order-2 flex w-full min-w-0 items-center gap-1 md:w-auto md:flex-none"
    >
      <!-- Search (always visible) -->
      <div class="relative flex min-w-0 flex-1 items-center md:flex-none">
        <div
          class="flex h-11 w-full min-w-0 items-center overflow-hidden rounded-md border border-n-weak bg-n-alpha-1 transition-colors focus-within:border-n-blue-7 focus-within:bg-n-solid-2 fine-pointer:h-8 md:w-40"
        >
          <Icon
            icon="i-lucide-search"
            class="size-3.5 text-n-slate-9 ml-2 flex-shrink-0"
          />
          <input
            id="pipeline-search"
            v-model="searchQuery"
            name="pipeline_search"
            type="search"
            class="w-full bg-transparent border-none outline-none ring-0 m-0 px-2 text-xs text-n-slate-12 placeholder:text-n-slate-9 reset-base no-margin"
            :placeholder="t('PIPELINE.SEARCH.INPUT_PLACEHOLDER')"
            :aria-label="t('PIPELINE.SEARCH.INPUT_PLACEHOLDER')"
            @input="onSearch"
          />
        </div>
        <!-- Search results badges -->
        <div
          v-if="searchQuery && searchResults.total > 0"
          class="ml-1 hidden flex-shrink-0 items-center gap-1 lg:flex"
        >
          <span
            class="flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-n-slate-12 text-n-background whitespace-nowrap"
          >
            {{ searchResults.total }}
          </span>
          <span
            v-for="(count, stageName) in searchResults.stages"
            :key="stageName"
            class="flex items-center gap-0.5 whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[10px] font-medium"
            :class="getStageColorClassesByName(stageName).badge"
          >
            {{ stageName }}
            <span class="opacity-70">{{ count }}</span>
          </span>
        </div>
      </div>

      <!-- Separator -->
      <div class="hidden md:block w-px h-4 bg-n-weak mx-0.5" />

      <!-- Bulk Actions -->
      <div class="relative inline-block bulk-actions-selector">
        <Popover
          ref="bulkActionsPopoverRef"
          align="end"
          disable-mobile-view
          :show-content-border="false"
          @hide="showBulkActions = false"
        >
          <Button
            ref="bulkActionsButtonRef"
            v-tooltip="{
              content: t('PIPELINE.BULK_ACTIONS.TITLE'),
              delay: { show: 400, hide: 0 },
              placement: 'bottom',
            }"
            variant="ghost"
            color="slate"
            size="xs"
            class="size-11 fine-pointer:size-8"
            :aria-label="t('PIPELINE.BULK_ACTIONS.TITLE')"
            aria-haspopup="menu"
            :aria-expanded="showBulkActions"
            aria-controls="pipeline-bulk-actions-menu"
            @click.stop="handleBulkActions"
          >
            <template #icon>
              <Icon icon="i-lucide-zap" class="size-3.5" />
            </template>
          </Button>

          <!-- Bulk Actions Dropdown -->
          <template #content>
            <Transition
              appear
              enter-active-class="transition ease-out duration-100"
              enter-from-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <div
                v-if="showBulkActions"
                id="pipeline-bulk-actions-menu"
                ref="bulkActionsDropdownRef"
                role="menu"
                :aria-label="t('PIPELINE.BULK_ACTIONS.TITLE')"
                class="min-w-[220px] overflow-y-auto rounded-xl border border-n-weak bg-n-solid-2 p-1 shadow-lg origin-top-right"
                @keydown="handleMenuKeydown($event, bulkActionsButtonRef)"
                @focusout="closeMenuAfterFocusLeaves"
              >
                <!-- UX B1 (2026-04-22): padronizado com `text-left`
                   no button (como o dropdown de Settings). Já tinha
                   `flex-1 text-left` no span do label desde antes. -->
                <button
                  v-for="action in filteredBulkActions"
                  :key="action.id"
                  type="button"
                  :role="
                    action.id === 'show_hidden'
                      ? 'menuitemcheckbox'
                      : 'menuitem'
                  "
                  :aria-checked="
                    action.id === 'show_hidden' ? showHiddenStages : undefined
                  "
                  class="w-full min-h-[44px] flex items-center gap-2.5 px-3 py-2 cursor-pointer rounded-lg text-sm text-n-slate-12 text-left hover:bg-n-alpha-2 transition-colors"
                  @click="selectBulkAction(action)"
                >
                  <Icon
                    :icon="getBulkActionIcon(action.icon)"
                    class="size-4 text-n-slate-10 flex-shrink-0"
                  />
                  <span class="flex-1 text-left">{{ action.label }}</span>
                  <span
                    v-if="action.id === 'show_hidden'"
                    class="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                    :class="{
                      'bg-n-blue-9 text-white': showHiddenStages,
                      'bg-n-alpha-2 text-n-slate-10': !showHiddenStages,
                    }"
                  >
                    {{
                      showHiddenStages
                        ? t('PIPELINE.TOGGLE_STATUS.ACTIVE')
                        : t('PIPELINE.TOGGLE_STATUS.INACTIVE')
                    }}
                  </span>
                </button>
              </div>
            </Transition>
          </template>
        </Popover>
      </div>

      <!-- Filter -->
      <Button
        variant="ghost"
        color="slate"
        size="xs"
        class="size-11 fine-pointer:size-8"
        data-tour="filter-button"
        :title="activeFiltersTooltip"
        :aria-label="t('PIPELINE.FILTER_ITEMS')"
        :aria-pressed="activeFiltersCount > 0"
        @click="$emit('filter')"
      >
        <template #icon>
          <Icon icon="i-lucide-filter" class="size-3.5" />
        </template>
        <span
          v-if="activeFiltersCount > 0"
          class="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-0.5 flex items-center justify-center text-[9px] font-semibold bg-n-blue-9 text-white rounded-full leading-none"
          :title="activeFiltersTooltip"
        >
          {{ activeFiltersCount }}
        </span>
      </Button>

      <!-- Separator -->
      <div class="hidden md:block w-px h-4 bg-n-weak mx-0.5" />

      <!-- Add item -->
      <div class="relative block">
        <Button
          ref="addButtonRef"
          variant="solid"
          color="blue"
          size="xs"
          class="pipeline-add-item-trigger size-11 fine-pointer:size-8"
          :aria-label="t('PIPELINE.ADD_ITEM')"
          aria-haspopup="menu"
          :aria-expanded="showAddDropdown"
          aria-controls="pipeline-add-menu"
          @click="handleAdd"
        >
          <template #icon>
            <Icon icon="i-lucide-plus" class="size-3.5" />
          </template>
        </Button>

        <!-- Add Dropdown -->
        <Transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="showAddDropdown"
            id="pipeline-add-menu"
            role="menu"
            :aria-label="t('PIPELINE.ADD_ITEM')"
            class="absolute right-0 top-full mt-1.5 bg-n-solid-2 rounded-xl shadow-lg border border-n-weak p-1 w-48 z-10 origin-top-right"
            @click.stop
            @keydown="handleMenuKeydown($event, addButtonRef)"
            @focusout="closeMenuAfterFocusLeaves"
          >
            <button
              type="button"
              role="menuitem"
              class="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2"
              @click="handleNewItem"
            >
              <Icon icon="i-lucide-plus" class="size-4 text-n-slate-10" />
              {{ t('PIPELINE.ADD_ITEM') }}
            </button>
            <button
              v-if="userIsAdmin"
              type="button"
              role="menuitem"
              class="flex min-h-11 w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-n-slate-12 transition-colors hover:bg-n-alpha-2"
              @click="handleNewPipeline"
            >
              <Icon icon="i-lucide-kanban" class="size-4 text-n-slate-10" />
              {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
            </button>
          </div>
        </Transition>
      </div>

      <Button
        variant="ghost"
        color="slate"
        size="xs"
        class="size-11 fine-pointer:size-8"
        :title="t('PIPELINE.KEYBOARD_SHORTCUTS.TITLE')"
        :aria-label="t('PIPELINE.KEYBOARD_SHORTCUTS.TITLE')"
        @click="showKeyboardShortcutsModal = true"
      >
        <template #icon>
          <Icon icon="i-lucide-circle-help" class="size-3.5" />
        </template>
      </Button>

      <!-- More options -->
      <div class="relative inline-block settings-selector">
        <Button
          ref="settingsButtonRef"
          variant="ghost"
          color="slate"
          size="xs"
          class="size-11 fine-pointer:size-8"
          :title="t('PIPELINE.MORE_OPTIONS.TITLE')"
          :aria-label="t('PIPELINE.MORE_OPTIONS.TITLE')"
          aria-haspopup="menu"
          :aria-expanded="showSettingsDropdown"
          aria-controls="pipeline-settings-menu"
          @click="handleSettingsClick"
        >
          <template #icon>
            <Icon icon="i-lucide-settings" class="size-3.5" />
          </template>
        </Button>

        <!-- Settings Dropdown -->
        <Transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="showSettingsDropdown"
            id="pipeline-settings-menu"
            role="menu"
            :aria-label="t('PIPELINE.MORE_OPTIONS.TITLE')"
            class="absolute right-0 top-full z-dropdown-overlay min-w-[220px] mt-1.5 p-1 rounded-xl shadow-lg bg-n-solid-2 border border-n-weak origin-top-right"
            @keydown="handleMenuKeydown($event, settingsButtonRef)"
            @focusout="closeMenuAfterFocusLeaves"
          >
            <!-- Bug 2 refinement (2026-04-22): adicionado `text-left`
                 explícito no button + `flex-1 text-left` no span do
                 label. Antes o button herdava `text-align: center`
                 do browser default — em algumas combinações de font/
                 zoom o item "Configurações do Pipeline" parecia
                 centralizado mesmo com flex layout. -->
            <!-- UX B2 (2026-04-22): + min-h-[44px] (WCAG 2.5.5 touch
                 target). Já tinha text-left desde Bug 2 refinement. -->
            <button
              v-for="option in settingsOptions"
              :key="option.id"
              type="button"
              role="menuitem"
              class="w-full min-h-[44px] flex items-center gap-2.5 px-3 py-2 cursor-pointer rounded-lg text-sm text-n-slate-12 text-left hover:bg-n-alpha-2 transition-colors"
              @click="handleSettingsOption(option)"
            >
              <Icon
                :icon="getSettingsIcon(option.icon)"
                class="size-4 text-n-slate-10 flex-shrink-0"
              />
              <span class="flex-1 text-left">{{ option.label }}</span>
            </button>
          </div>
        </Transition>
      </div>
    </div>

    <!-- Menu móvel para elementos ocultos -->
    <!-- Navbar inferior removida conforme solicitado -->

    <!-- Modal para seleção de visualização -->
    <Modal
      v-model:show="showViewModal"
      size="tiny"
      @close="() => (showViewModal = false)"
    >
      <div class="p-4">
        <h3 class="text-lg font-medium mb-4 text-n-slate-12">
          {{ t('PIPELINE.VIEW_MODES.SELECT_VIEW') }}
        </h3>
        <div class="flex flex-col gap-3">
          <button
            type="button"
            class="flex min-h-11 items-center gap-3 rounded-lg p-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
            :class="{
              'bg-n-blue-3 text-n-blue-11': currentView === 'pipeline',
              'hover:bg-n-alpha-2': currentView !== 'pipeline',
            }"
            :aria-pressed="currentView === 'pipeline'"
            @click="
              handleViewChange('pipeline');
              showViewModal = false;
            "
          >
            <Icon icon="i-lucide-kanban" class="size-5" />
            <div>
              <div class="font-medium">
                {{ t('PIPELINE.VIEW_MODES.PIPELINE') }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ t('PIPELINE.VIEW_MODES.PIPELINE_DESC') }}
              </div>
            </div>
          </button>

          <button
            type="button"
            class="flex min-h-11 items-center gap-3 rounded-lg p-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
            :class="{
              'bg-n-blue-3 text-n-blue-11': currentView === 'list',
              'hover:bg-n-alpha-2': currentView !== 'list',
            }"
            :aria-pressed="currentView === 'list'"
            @click="
              handleViewChange('list');
              showViewModal = false;
            "
          >
            <Icon icon="i-lucide-list" class="size-5" />
            <div>
              <div class="font-medium">{{ t('PIPELINE.VIEW_MODES.LIST') }}</div>
              <div class="text-xs text-n-slate-10">
                {{ t('PIPELINE.VIEW_MODES.LIST_DESC') }}
              </div>
            </div>
          </button>

          <button
            type="button"
            class="flex min-h-11 items-center gap-3 rounded-lg p-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
            :class="{
              'bg-n-blue-3 text-n-blue-11': currentView === 'agenda',
              'hover:bg-n-alpha-2': currentView !== 'agenda',
            }"
            :aria-pressed="currentView === 'agenda'"
            @click="
              handleViewChange('agenda');
              showViewModal = false;
            "
          >
            <Icon icon="i-lucide-calendar" class="size-5" />
            <div>
              <div class="font-medium">
                {{ t('PIPELINE.VIEW_MODES.AGENDA') }}
              </div>
              <div class="text-xs text-n-slate-10">
                {{ t('PIPELINE.VIEW_MODES.AGENDA_DESC') }}
              </div>
            </div>
          </button>

          <button
            type="button"
            class="flex min-h-11 items-center gap-3 rounded-lg p-3 text-left hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
            :class="{
              'bg-n-blue-3 text-n-blue-11': currentView === 'dashboard',
            }"
            :aria-pressed="currentView === 'dashboard'"
            @click="
              handleViewChange('dashboard');
              showViewModal = false;
            "
          >
            <Icon icon="i-lucide-layout-dashboard" class="size-5" />
            <div>
              <div class="font-medium">{{ t('PIPELINE.DASHBOARD.TITLE') }}</div>
              <div class="text-xs text-n-slate-10">
                {{ t('PIPELINE.DASHBOARD.SUBTITLE') }}
              </div>
            </div>
          </button>
        </div>
      </div>
    </Modal>

    <Modal
      v-model:show="showAddModal"
      :before-close="requestAddCardModalClose"
      @close="() => (showAddModal = false)"
    >
      <div class="flex flex-col max-h-[85vh] overflow-hidden">
        <div class="px-5 pt-5 pb-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ t('PIPELINE.ADD_ITEM') }}
          </h3>
        </div>
        <PipelineCardForm
          v-if="selectedPipeline"
          ref="addCardFormRef"
          :pipeline-id="selectedPipeline.id"
          :stage="currentStage"
          @saved="handleItemCreated"
          @close="showAddModal = false"
        />
        <div v-else class="text-center text-red-500 p-6">
          {{ t('PIPELINE.ERRORS.NO_PIPELINE_SELECTED') }}
        </div>
      </div>
    </Modal>

    <PipelineSettings :show="showSettingsModal" @close="handleCloseSettings" />

    <AIConfigModal
      v-if="showAIConfigModal"
      @close="showAIConfigModal = false"
    />

    <KeyboardShortcutsModal
      :show="showKeyboardShortcutsModal"
      @close="showKeyboardShortcutsModal = false"
    />

    <PipelineCardImportModal
      v-model:show="showImportModal"
      :pipeline-id="selectedPipeline?.id"
      @imported="emit('itemsUpdated')"
    />

    <PipelineTrashModal
      v-model:show="showTrashModal"
      :pipeline-id="selectedPipeline?.id"
    />

    <PipelineFollowUpRules
      :show="showFollowUpRulesModal"
      :pipeline="selectedPipeline"
      @close="showFollowUpRulesModal = false"
    />

    <Modal
      v-model:show="showWebhooksModal"
      size="large"
      @close="() => (showWebhooksModal = false)"
    >
      <PipelineWebhooks
        :pipelines="selectedPipeline ? [selectedPipeline] : []"
        @close="showWebhooksModal = false"
      />
    </Modal>

    <Modal
      :show="showBulkDeleteModal"
      @update:show="updateBulkDeleteModal"
      @close="updateBulkDeleteModal(false)"
    >
      <BulkDeleteModal
        :items="pipelineCards"
        :is-submitting="isBulkSubmitting"
        @close="updateBulkDeleteModal(false)"
        @confirm="handleBulkDelete"
      />
    </Modal>

    <Modal
      :show="showBulkPriorityModal"
      @update:show="updateBulkPriorityModal"
      @close="updateBulkPriorityModal(false)"
    >
      <BulkPriorityModal
        :items="pipelineCards"
        :is-submitting="isBulkSubmitting"
        @close="updateBulkPriorityModal(false)"
        @confirm="handleBulkSetPriority"
      />
    </Modal>

    <Modal
      :show="showBulkMoveModal"
      @update:show="updateBulkMoveModal"
      @close="updateBulkMoveModal(false)"
    >
      <BulkMoveModal
        :items="pipelineCards"
        :is-submitting="isBulkSubmitting"
        @close="updateBulkMoveModal(false)"
        @confirm="handleBulkMove"
      />
    </Modal>

    <Modal
      v-model:show="showBulkAddModal"
      @close="() => (showBulkAddModal = false)"
    >
      <BulkAddModal
        :current-stage="currentStage"
        @close="showBulkAddModal = false"
        @items-created="handleBulkItemsCreated"
      />
    </Modal>

    <!-- Modal de Novo Funil -->
    <Modal
      v-model:show="showNewPipelineModal"
      size="large"
      :before-close="requestNewPipelineModalClose"
      @close="() => (showNewPipelineModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.PIPELINES.ACTIONS.NEW') }}
        </h3>
        <PipelineForm
          ref="newPipelineFormRef"
          @saved="handlePipelineSaved"
          @close="showNewPipelineModal = false"
        />
      </div>
    </Modal>

    <!-- Modal de Envio de Mensagem -->
    <Modal
      v-model:show="showSendMessageModal"
      size="medium"
      @close="() => (showSendMessageModal = false)"
    >
      <div class="p-6">
        <h3 class="text-lg font-medium mb-4">
          {{ t('PIPELINE.BULK_ACTIONS.SEND_MESSAGE.MODAL_TITLE') }}
        </h3>
        <BulkSendMessageModal
          :items="itemsWithConversation"
          @close="showSendMessageModal = false"
          @send="handleMessageSent"
        />
      </div>
    </Modal>
  </header>
</template>
